/**
 * Fetches the portal configuration (`/bootstrap`), retrying what a moment later would
 * succeed.
 *
 * The request needs no token, so when it fails it is almost never the endpoint: it is the
 * document loading while the network is not there yet. An idle tab is reloaded exactly
 * then — a renewal that failed on wake sends it to /login, Chrome restores a tab it
 * discarded — and a single attempt turned that blip into the maintenance page. A gateway
 * 5xx answered without CORS headers fails the same way and is just as short-lived.
 */

/** Pauses before the 2nd, 3rd and 4th attempt. Four attempts in about 7s plus timeouts. */
export const RETRY_DELAYS_MS = [1_000, 2_000, 4_000];

/** One attempt never waits longer than this: a request stuck on a dead connection. */
export const ATTEMPT_TIMEOUT_MS = 10_000;

/** How long an attempt waits for the browser to come back online before trying anyway. */
export const ONLINE_WAIT_MS = 15_000;

/** Every attempt failed. `cause` is the last attempt's error. */
export class BootstrapUnavailableError extends Error {
  readonly cause: unknown;
  readonly attempts: number;

  constructor(attempts: number, cause: unknown) {
    const reason = cause instanceof Error ? cause.message : String(cause);
    super(`Bootstrap unavailable after ${attempts} attempts: ${reason}`);
    this.name = 'BootstrapUnavailableError';
    this.cause = cause;
    this.attempts = attempts;
  }
}

/**
 * The configuration answered with an error status whose body announces maintenance. Not
 * retried: the server is reachable, and it is telling us why it will not serve.
 */
export class BootstrapMaintenanceError extends Error {
  readonly status: number;

  constructor(status: number) {
    super(`Bootstrap answered HTTP ${status} in maintenance mode`);
    this.name = 'BootstrapMaintenanceError';
    this.status = status;
  }
}

/** Whether an error response's body is the maintenance announcement. Any other body is not. */
async function announcesMaintenance(response: Response): Promise<boolean> {
  try {
    const body = await response.json();
    return Boolean(body?.MAINTENANCE_MODE);
  } catch {
    return false;
  }
}

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Resolves once the browser reports itself online, or after `maxWaitMs`. `navigator.onLine`
 * is only trusted when it says offline; online it may still be wrong, which the attempt
 * itself finds out.
 */
export function waitUntilOnline(maxWaitMs: number = ONLINE_WAIT_MS): Promise<void> {
  if (typeof navigator === 'undefined' || navigator.onLine !== false) return Promise.resolve();
  return new Promise((resolve) => {
    const done = () => {
      clearTimeout(timer);
      window.removeEventListener('online', done);
      resolve();
    };
    const timer = setTimeout(done, maxWaitMs);
    window.addEventListener('online', done);
  });
}

/**
 * One request. Rejects on a network error, a timeout, a non-2xx status or a body that is not
 * an object — with a `BootstrapMaintenanceError` when the non-2xx body announces maintenance.
 */
export async function fetchBootstrapOnce(
  url: string,
  timeoutMs: number = ATTEMPT_TIMEOUT_MS
): Promise<Record<string, any>> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(url, { signal: controller.signal });
    if (!response.ok) {
      if (await announcesMaintenance(response))
        throw new BootstrapMaintenanceError(response.status);
      throw new Error(`HTTP ${response.status}`);
    }
    const json = await response.json();
    if (!json || typeof json !== 'object') throw new Error('Empty bootstrap response');
    return json;
  } finally {
    clearTimeout(timer);
  }
}

export async function loadBootstrap(
  url: string,
  delaysMs: number[] = RETRY_DELAYS_MS
): Promise<Record<string, any>> {
  const attempts = delaysMs.length + 1;
  let lastError: unknown;
  for (let attempt = 0; attempt < attempts; attempt++) {
    if (attempt > 0) {
      console.warn('[Bootstrap]', `attempt ${attempt} failed, retrying:`, lastError);
      await sleep(delaysMs[attempt - 1]);
    }
    await waitUntilOnline();
    try {
      return await fetchBootstrapOnce(url);
    } catch (error) {
      if (error instanceof BootstrapMaintenanceError) throw error;
      lastError = error;
    }
  }
  throw new BootstrapUnavailableError(attempts, lastError);
}
